import { zodResolver } from '@hookform/resolvers/zod'
import type React from 'react'
import { type RefObject, useEffect } from 'react'
import {
	FormProvider,
	type SubmitHandler,
	useFieldArray,
	useForm
} from 'react-hook-form'
import toast from 'react-hot-toast'
import { useOutletContext } from 'react-router'
import { CharCounter } from '@/components/commons/char-counter'
import { InputText } from '@/components/commons/input-text'
import { TextArea } from '@/components/commons/textarea'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { ArrowRightIcon, Stars01Icon, User01Icon } from '@/components/ui/icons'
import { Separator } from '@/components/ui/separator'
import { Spinner } from '@/components/ui/spinner'
import type { OpsVettingURLParams } from '@/features/ops-vetting/types'
import {
	SERVICE_OUTPUT_CHARACTER_LIMITS,
	ServiceMerchandisingOutputSchema
} from '@/features/service-vetting/form-schemas.ts'
import { cn, errorHandler } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'
import { OpsVettingMerchFormHeader } from '../../ops-vetting/components/ops-vetting-merch-form-header'
import { useSaveServiceMerchVetting } from '../query/use-save-service-merch-vetting'
import { getOfferingTypeLabel } from '../utils'
import { ServiceVettingMerchFormSkeleton } from './service-vetting-merch-form-skeleton'

interface ServiceVettingMerchOutputFormProps {
	isLoading?: boolean
	isReadOnly?: boolean
	isHostEdits?: boolean
	bgStyle?: 'light' | 'dark'
	defaultValues?: ServiceMerchandisingOutputSchema
	finalSubmitRef: RefObject<HTMLButtonElement | null>
	handleFinalSubmissionCb: SubmitHandler<ServiceMerchandisingOutputSchema>
}

interface HeaderConfig {
	variant: 'base' | 'blue'
	icon: React.ReactElement
	title: string
	description: string
	showSaveButton: boolean
}

interface SaveButtonProps {
	onClick: () => void
	disabled: boolean
	isPending: boolean
	label: string
}

const SaveButton = ({
	onClick,
	disabled,
	isPending,
	label
}: SaveButtonProps) => (
	<ButtonLegacy
		disabled={disabled}
		onClick={onClick}
		size={'sm'}
		type='button'
		variant={'secondary'}
	>
		{isPending && <Spinner />}
		{label}
	</ButtonLegacy>
)

export const ServiceVettingMerchOutputForm = ({
	isLoading = false,
	isReadOnly = false,
	isHostEdits = false,
	bgStyle = 'dark',
	defaultValues,
	finalSubmitRef,
	handleFinalSubmissionCb
}: ServiceVettingMerchOutputFormProps) => {
	const { t } = useTranslation()
	const params = useOutletContext<{ urlParams: OpsVettingURLParams }>()
	const serviceId = params?.urlParams.id
	const methods = useForm({
		resolver: zodResolver(ServiceMerchandisingOutputSchema),
		defaultValues
	})

	const {
		register,
		control,
		reset,
		getValues,
		formState: { errors }
	} = methods
	const { fields } = useFieldArray({ control, name: 'offerings' })

	useEffect(() => {
		if (defaultValues) {
			reset(defaultValues)
		}
	}, [defaultValues, reset])

	const {
		mutate: saveServiceMerchVetting,
		isPending: isSavingServiceMerchVetting
	} = useSaveServiceMerchVetting(
		{ serviceId },
		{
			onSuccess: () =>
				toast.success(
					t('vetting:merch.service.generated_content_saved_tooltip')
				),
			onError: (error) => errorHandler(error)
		}
	)

	const handleSaveServiceMerchVetting = () => {
		const payload = {
			...getValues(),
			is_host_approval_needed: false,
			is_waitlisted: false
		}
		saveServiceMerchVetting(payload)
	}

	const getHeaderConfig = (): HeaderConfig => {
		if (isReadOnly && !isHostEdits) {
			return {
				variant: 'base',
				icon: <ArrowRightIcon />,
				title: t('vetting:merch.service.output_form_title_readonly'),
				description: t(
					'vetting:merch.service.output_form_description_readonly'
				),
				showSaveButton: false
			}
		}

		if (isHostEdits) {
			return {
				variant: 'blue',
				icon: <User01Icon />,
				title: t('vetting:merch.service.output_form_title_host_edits'),
				description: t(
					'vetting:merch.service.output_form_description_host_edits'
				),
				showSaveButton: !isReadOnly && !!serviceId
			}
		}

		return {
			variant: 'blue',
			icon: <Stars01Icon />,
			title: t('vetting:merch.service.output_form_title'),
			description: t('vetting:merch.service.output_form_description'),
			showSaveButton: !!serviceId
		}
	}

	const headerConfig = getHeaderConfig()

	return (
		<FormProvider {...methods}>
			<form
				className={cn(
					'h-full flex-1 p-6',
					bgStyle === 'light' ? 'bg-neutral-white' : 'bg-neutral-50'
				)}
				onSubmit={methods.handleSubmit(handleFinalSubmissionCb)}
			>
				<OpsVettingMerchFormHeader
					description={headerConfig.description}
					endSlot={
						headerConfig.showSaveButton ? (
							<SaveButton
								disabled={
									isSavingServiceMerchVetting || isLoading
								}
								isPending={isSavingServiceMerchVetting}
								label={t('common:save')}
								onClick={handleSaveServiceMerchVetting}
							/>
						) : undefined
					}
					icon={headerConfig.icon}
					title={headerConfig.title}
					variant={headerConfig.variant}
				/>

				{isLoading ? (
					<ServiceVettingMerchFormSkeleton />
				) : (
					<div className='flex w-full flex-col gap-2'>
						<div className='flex w-full flex-col gap-5 py-6 text-neutral-900'>
							<h4 className='font-medium text-base'>
								{t(
									'vetting:merch.service.form_offering_section_title'
								)}
							</h4>
							<div className='flex w-full flex-col'>
								<div className='flex w-full flex-col'>
									<InputText
										disabled={isReadOnly}
										label={t(
											'vetting:merch.service.service_title'
										)}
										type={'text'}
										{...register('service_title')}
									/>
									<CharCounter<ServiceMerchandisingOutputSchema>
										maxLength={
											SERVICE_OUTPUT_CHARACTER_LIMITS
												.SERVICE.TITLE
										}
										name='service_title'
									/>
								</div>
							</div>

							<div className='flex w-full flex-col'>
								<TextArea
									disabled={isReadOnly}
									label={t(
										'vetting:merch.service.service_description'
									)}
									rows={3}
									{...register('service_description')}
									errorMessage={
										errors?.service_description?.message
									}
								/>
								<CharCounter<ServiceMerchandisingOutputSchema>
									maxLength={
										SERVICE_OUTPUT_CHARACTER_LIMITS.SERVICE
											.DESCRIPTION
									}
									name='service_description'
								/>
							</div>

							{fields.map((offering, index) => (
								<div
									className='flex w-full flex-col gap-3'
									key={offering.id}
								>
									<p className='font-medium text-sm'>
										{t(getOfferingTypeLabel(offering.type))}
										{t(
											'vetting:merch.service.offering_duration',
											{
												duration:
													offering.duration ?? '-'
											}
										)}
									</p>
									<div className='flex w-full flex-col'>
										<InputText
											disabled={isReadOnly}
											label={t(
												'vetting:merch.service.offering_title'
											)}
											type={'text'}
											{...register(
												`offerings.${index}.improved_title`
											)}
										/>
										<CharCounter<ServiceMerchandisingOutputSchema>
											maxLength={
												SERVICE_OUTPUT_CHARACTER_LIMITS
													.OFFERING.TITLE
											}
											name={`offerings.${index}.improved_title`}
										/>
									</div>

									<div className='flex w-full flex-col'>
										<TextArea
											disabled={isReadOnly}
											label={t(
												'vetting:merch.service.offering_description_label'
											)}
											rows={4}
											{...register(
												`offerings.${index}.improved_description`
											)}
										/>
										<CharCounter<ServiceMerchandisingOutputSchema>
											maxLength={
												SERVICE_OUTPUT_CHARACTER_LIMITS
													.OFFERING.DESCRIPTION
											}
											name={`offerings.${index}.improved_description`}
										/>
									</div>
								</div>
							))}
						</div>

						<Separator orientation='horizontal' />

						<div className='flex w-full flex-col gap-5 py-6 text-neutral-900'>
							<h4 className='font-medium text-base'>
								{t(
									'vetting:merch.service.form_host_section_title'
								)}
							</h4>
							<div className='flex w-full flex-col'>
								<InputText
									disabled={isReadOnly}
									label={t(
										'vetting:merch.service.host_experience_title'
									)}
									type={'text'}
									{...register('host_experience_title')}
									errorMessage={
										errors?.host_experience_title?.message
									}
								/>
								<CharCounter<ServiceMerchandisingOutputSchema>
									maxLength={
										SERVICE_OUTPUT_CHARACTER_LIMITS.HOST
											.EXPERIENCE.TITLE
									}
									name='host_experience_title'
								/>
							</div>

							<div className='flex w-full flex-col'>
								<TextArea
									disabled={isReadOnly}
									label={t(
										'vetting:merch.service.host_experience_description'
									)}
									rows={3}
									{...register('host_experience_description')}
									errorMessage={
										errors?.host_experience_description
											?.message
									}
								/>
								<CharCounter<ServiceMerchandisingOutputSchema>
									maxLength={
										SERVICE_OUTPUT_CHARACTER_LIMITS.HOST
											.EXPERIENCE.DESCRIPTION
									}
									name='host_experience_description'
								/>
							</div>

							<div className='flex w-full flex-col'>
								<InputText
									disabled={isReadOnly}
									label={t(
										'vetting:merch.service.host_career_highlight_title'
									)}
									type={'text'}
									{...register('host_career_highlight_title')}
									errorMessage={
										errors?.host_career_highlight_title
											?.message
									}
								/>
								<CharCounter<ServiceMerchandisingOutputSchema>
									maxLength={
										SERVICE_OUTPUT_CHARACTER_LIMITS.HOST
											.CAREER_HIGHLIGHT.TITLE
									}
									name='host_career_highlight_title'
								/>
							</div>

							<div className='flex w-full flex-col'>
								<TextArea
									disabled={isReadOnly}
									label={t(
										'vetting:merch.service.host_career_highlight_description'
									)}
									rows={3}
									{...register(
										'host_career_highlight_description'
									)}
									errorMessage={
										errors
											?.host_career_highlight_description
											?.message
									}
								/>
								<CharCounter<ServiceMerchandisingOutputSchema>
									maxLength={
										SERVICE_OUTPUT_CHARACTER_LIMITS.HOST
											.CAREER_HIGHLIGHT.DESCRIPTION
									}
									name='host_career_highlight_description'
								/>
							</div>

							<div className='flex w-full flex-col'>
								<InputText
									disabled={isReadOnly}
									label={t(
										'vetting:merch.service.host_education_title'
									)}
									type={'text'}
									{...register('host_education_title')}
									errorMessage={
										errors?.host_education_title?.message
									}
								/>
								<CharCounter<ServiceMerchandisingOutputSchema>
									maxLength={
										SERVICE_OUTPUT_CHARACTER_LIMITS.HOST
											.EDUCATION.TITLE
									}
									name='host_education_title'
								/>
							</div>

							<div className='flex w-full flex-col'>
								<TextArea
									disabled={isReadOnly}
									label={t(
										'vetting:merch.service.host_education_description'
									)}
									rows={3}
									{...register('host_education_description')}
									errorMessage={
										errors?.host_education_description
											?.message
									}
								/>
								<CharCounter<ServiceMerchandisingOutputSchema>
									maxLength={
										SERVICE_OUTPUT_CHARACTER_LIMITS.HOST
											.EDUCATION.DESCRIPTION
									}
									name='host_education_description'
								/>
							</div>
						</div>
					</div>
				)}
				<button
					ref={finalSubmitRef}
					style={{ display: 'none' }}
					type='submit'
				/>
			</form>
		</FormProvider>
	)
}
